import { Alert, Button, Card, Empty, Space, Spin, Tag, Typography } from 'antd';
import React, { useCallback } from 'react';

import { useSkillsList, type SkillGridItem } from '@/hooks/useSkillsList';

const { Paragraph, Text, Title } = Typography;

const SKILL_DATETIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const formatSkillCreatedAt = (isoDate: string): string => {
  try {
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) {
      return isoDate;
    }
    return SKILL_DATETIME_FORMATTER.format(date);
  } catch {
    return isoDate;
  }
};

/** Plain-text excerpt from markdown for card previews (best-effort strip). */
const excerptFromMarkdown = (markdown: string, maxLen: number): string => {
  let s = markdown.replaceAll('\r\n', '\n');
  s = s.replaceAll(/```[\s\S]*?```/g, ' ');
  s = s.replaceAll(/`([^`]+)`/g, '$1');
  s = s.replaceAll(/\[([^\]]+)\]\([^)]*\)/g, '$1');
  s = s.replaceAll(/^#{1,6}\s+/gm, '');
  s = s.replaceAll(/\*\*([^*]+)\*\*/g, '$1');
  s = s.replaceAll(/\*([^*]+)\*/g, '$1');
  s = s.replaceAll(/_{1,2}([^_]+)_{1,2}/g, '$1');
  s = s.replaceAll(/\s+/g, ' ').trim();
  if (!s) {
    return 'No description yet.';
  }
  if (s.length <= maxLen) {
    return s;
  }
  return `${s.slice(0, Math.max(0, maxLen - 1))}…`;
};

function templateBundledBylineParts(item: Extract<SkillGridItem, { kind: 'template' }>): string[] {
  const parts: string[] = [
    item.metaVersion
      ? `Bundled with the platform · v${item.metaVersion}`
      : 'Bundled with the platform',
  ];
  const au = item.author?.trim();
  if (au) {
    parts.push(au);
  }
  return parts;
}

export interface SkillsPageProps {
  accessToken: string | null | undefined;
  onOpenTemplate: (templateKey: string) => void;
  onOpenSkillVersion: (versionId: string) => void;
  onCreateNew: () => void;
}

export const SkillsPage: React.FC<SkillsPageProps> = ({
  accessToken,
  onOpenTemplate,
  onOpenSkillVersion,
  onCreateNew,
}) => {
  const { loading, error, gridItems } = useSkillsList(accessToken);

  const handleCardActivate = useCallback(
    (item: SkillGridItem) => {
      if (item.kind === 'template') {
        onOpenTemplate(item.templateKey);
      } else {
        onOpenSkillVersion(item.row.id);
      }
    },
    [onOpenSkillVersion, onOpenTemplate],
  );

  if (!accessToken) {
    return <Alert type="warning" showIcon message="Sign in to view skills." />;
  }

  let libraryBody: React.ReactNode;
  if (loading) {
    libraryBody = <Spin />;
  } else if (gridItems.length === 0) {
    libraryBody = (
      <Empty description="No skills yet — create a new skill or add templates to the platform bundle." />
    );
  } else {
    libraryBody = (
      <div className="skills-page__grid">
        {gridItems.map((item) => {
          const key = item.kind === 'template' ? `t:${item.templateKey}` : `u:${item.row.id}`;
          const isTemplate = item.kind === 'template';
          const titleText = isTemplate ? item.title : item.row.title;
          const tagLabel = isTemplate ? 'Default' : 'User Provided';
          let bodyDescription: string;
          if (item.kind === 'template') {
            const metaDesc = item.description.trim();
            bodyDescription =
              metaDesc ||
              'Platform template — open to view the full skill body and fork if needed.';
          } else {
            bodyDescription =
              item.row.description?.trim() || excerptFromMarkdown(item.row.body_markdown, 220);
          }
          let createdLine = '';
          let authorLabel = '';
          if (item.kind === 'user') {
            createdLine = `Created ${formatSkillCreatedAt(item.row.created_at)}`;
            authorLabel = item.row.version_author_name?.trim() || 'Unknown author';
          }
          const templateBylineParts: string[] =
            item.kind === 'template' ? templateBundledBylineParts(item) : [];

          return (
            <Card
              key={key}
              tabIndex={0}
              hoverable
              className="skills-page__card"
              onClick={() => handleCardActivate(item)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleCardActivate(item);
                }
              }}
            >
              <div className="skills-page__card-head">
                <Title level={5} className="skills-page__card-title">
                  {titleText}
                </Title>
                <Tag color={isTemplate ? 'default' : 'green'}>{tagLabel}</Tag>
              </div>
              {item.kind === 'template' && templateBylineParts.length > 0 ? (
                <Text type="secondary" className="skills-page__card-byline">
                  {templateBylineParts.join(' · ')}
                </Text>
              ) : null}
              {item.kind === 'template' && item.tags.length > 0 ? (
                <div className="skills-page__card-tags" style={{ marginBottom: 0 }}>
                  <Space size={[4, 4]} wrap>
                    {item.tags.slice(0, 6).map((tg) => (
                      <Tag key={tg}>{tg}</Tag>
                    ))}
                  </Space>
                </div>
              ) : null}
              <Paragraph ellipsis={{ rows: 3 }} className="skills-page__card-desc">
                {bodyDescription}
              </Paragraph>
              {item.kind === 'user' ? (
                <div className="skills-page__card-meta-stack">
                  <Text type="secondary" className="skills-page__card-meta">
                    {createdLine}
                  </Text>
                  <Text type="secondary" className="skills-page__card-author">
                    Created by {authorLabel}
                  </Text>
                </div>
              ) : null}
            </Card>
          );
        })}
      </div>
    );
  }

  return (
    <div className="skills-page">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div className="skills-page__header-row">
          <div className="skills-page__header-text">
            <Title level={3} style={{ marginTop: 0 }}>
              Skills
            </Title>
            <Text type="secondary">
              Browse default templates and skills you have created or edited. One shared library for
              all authenticated users.
            </Text>
          </div>
          <div className="skills-page__header-actions">
            <Button type="primary" onClick={onCreateNew} disabled={loading}>
              New skill
            </Button>
          </div>
        </div>

        {error ? <Alert type="error" showIcon message={error} /> : null}

        {libraryBody}
      </Space>
    </div>
  );
};
